import { type RefObject, useCallback, useEffect, useState } from 'react';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils/classNames';

import Icon from '@/components/base/Icon';
import type { InfiniteScrollRef } from '@/components/composite/InfiniteScroll/InfiniteScroll';

const DISTANCE_THRESHOLD_PX = 48;
const SCROLL_END_DEBOUNCE_MS = 150;

export type SparksScrollToBottomProps = {
    scrollRef: RefObject<InfiniteScrollRef | null>;
    /** When 0, the affordance stays hidden and scroll listeners are not attached. */
    messageCount: number;
    /** Re-sync when loading state changes (e.g. conversation switch). */
    isLoading?: boolean;
};

export function SparksScrollToBottom({
    scrollRef,
    messageCount,
    isLoading = false
}: SparksScrollToBottomProps) {
    const { t } = useTranslation('sparks');
    const [hasContentBelow, setHasContentBelow] = useState(false);
    const [hideWhileScrolling, setHideWhileScrolling] = useState(false);

    const show = messageCount > 0 && hasContentBelow && !hideWhileScrolling;

    useEffect(() => {
        if (messageCount === 0) {
            setHasContentBelow(false);
            return;
        }

        let cancelled = false;
        let rafId = 0;
        let detach: (() => void) | undefined;

        const tryAttach = () => {
            const el = scrollRef.current?.getScrollElement() ?? null;
            if (!el || cancelled) {
                if (!cancelled) {
                    rafId = requestAnimationFrame(tryAttach);
                }
                return;
            }

            let scrollEndTimer: ReturnType<typeof setTimeout> | null = null;

            const updateDistance = () => {
                const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
                setHasContentBelow(distance > DISTANCE_THRESHOLD_PX);
            };

            const onScroll = () => {
                setHideWhileScrolling(true);
                updateDistance();
                if (scrollEndTimer) clearTimeout(scrollEndTimer);
                scrollEndTimer = setTimeout(
                    () => setHideWhileScrolling(false),
                    SCROLL_END_DEBOUNCE_MS
                );
            };

            updateDistance();
            el.addEventListener('scroll', onScroll, { passive: true });
            const ro = new ResizeObserver(() => updateDistance());
            ro.observe(el);

            detach = () => {
                el.removeEventListener('scroll', onScroll);
                ro.disconnect();
                if (scrollEndTimer) clearTimeout(scrollEndTimer);
            };
        };

        tryAttach();

        return () => {
            cancelled = true;
            cancelAnimationFrame(rafId);
            detach?.();
        };
    }, [messageCount, isLoading]);

    const handleClick = useCallback(() => {
        scrollRef.current?.scrollToBottom({ behavior: 'smooth' });
    }, [scrollRef]);

    return (
        <div
            className={classNames(
                'pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center bg-gradient-to-b from-transparent to-white py-3 dark:to-icoDarkMode-deepBlack',
                'transform-gpu transition-[opacity,transform] duration-300 ease-in-out',
                'motion-reduce:transition-none motion-reduce:duration-0',
                show
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-2 opacity-0 motion-reduce:translate-y-0'
            )}
            aria-hidden={!show}>
            <button
                type="button"
                onClick={handleClick}
                tabIndex={show ? 0 : -1}
                className={classNames(
                    'pointer-events-auto flex size-7 shrink-0 items-center justify-center rounded-full bg-icoGray-300 text-icoBlack shadow-icoXS transition hover:opacity-90 dark:bg-icoDarkMode-beton dark:text-white',
                    !show && 'pointer-events-none'
                )}
                aria-label={t('scroll_to_bottom')}>
                <Icon icon="chevron-bottom" className="text-[14px]" />
            </button>
        </div>
    );
}
